/**
 * Which clinics have traffic analytics, and which reports each can answer.
 *
 * Mirrors backend/config/bigquery-brands.json — a clinic gets the traffic and
 * attribution reports when it has a Segment dataset in BigQuery. Absent event
 * tables no longer break these pages (the backend substitutes an empty
 * stand-in), so presence of a dataset is the whole test.
 *
 * Kept deliberately as a flat list rather than derived from the clinic list:
 * a clinic exists in core long before Segment is wired up for it, and showing
 * a report that can only ever be empty is worse than not showing it.
 */

/** Clinics with a BigQuery dataset. Keep in step with bigquery-brands.json. */
export const TRAFFIC_CLINICS = [
  'tmates',
  'mdexam',
  'mdexam_legacy',
  'rose',
  'mdrx',
  'vigore',
  'evamd',
  'revora',
  'trybalance',
  'scienceco',
  'rxbloom',
]

/**
 * Clinics whose ad spend is synced into ad_platform.ad_spend.
 *
 * The spend-dependent reports (Marketing, Channels, Product × Channel, Lift,
 * Growth Lanes) still render for everyone else, but with no spend to join
 * against they cannot show cost, CAC or ROAS — the pages surface an explicit
 * "no ad spend connected" state rather than a column of silent zeros.
 */
export const AD_SPEND_CLINICS = ['tmates', 'mdexam', 'mdexam_legacy', 'mdrx', 'scienceco']

/**
 * Clinics that can answer LP Click Rate.
 *
 * The metric is a click-through from a marketing page to the intake, so it
 * needs the two to be distinguishable. What it does NOT need is two datasets:
 * Science.co serves science.co, intake.science.co and portal.science.co from a
 * single Segment source, and the hosts separate them perfectly well — which is
 * why it is here despite having one dataset. MDExam uses a CTA event instead of
 * a host crossing. The brands still missing are the ones that serve marketing
 * and funnel from one host, where there is no boundary to cross at all.
 */
export const LP_CLICK_RATE_CLINICS = ['tmates', 'mdexam', 'mdexam_legacy', 'scienceco']

export function hasTrafficAnalytics(clinicIds = []) {
  return clinicIds.some(id => TRAFFIC_CLINICS.includes(id))
}

export function hasAdSpend(clinicIds = []) {
  return clinicIds.some(id => AD_SPEND_CLINICS.includes(id))
}
